import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

export interface PickerOption {
  value: string
  label: string
  detail?: string
  content?: ReactNode
  disabled?: boolean
}

type PickerProps = {
  options: PickerOption[]
  label: string
  disabled?: boolean
  required?: boolean
  id?: string
  field?: boolean
  triggerContent?: ReactNode
  triggerClassName?: string
} & ({ multiple?: false; value: string; onChange: (value: string) => void }
  | { multiple: true; value: string[]; onChange: (value: string[]) => void })

/** Selection menus share positioning, keyboard navigation and platform styling. */
export function Picker(props: PickerProps) {
  const { options, value, label, disabled = false, required = false, id: suppliedId, field = false, triggerContent, triggerClassName } = props
  const [open, setOpen] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [position, setPosition] = useState<CSSProperties>({ visibility: 'hidden' })
  const container = useRef<HTMLSpanElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const search = useRef({ text: '', time: 0 })
  const generatedId = useId()
  const id = suppliedId ?? generatedId
  const selected = options.find(option => option.value === value)
  const picked = props.multiple ? props.value : [props.value]
  const selectedLabel = props.multiple ? `${picked.length} selected` : selected?.label ?? 'Choose…'
  const allValues = options.filter(option => !option.disabled).map(option => option.value)
  const allSelected = allValues.length > 0 && allValues.every(item => picked.includes(item))
  const expanded = open && !disabled

  useLayoutEffect(() => {
    if (!expanded) return
    const place = () => {
      const rect = trigger.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(Math.max(304, rect.width), window.innerWidth - 24)
      const below = window.innerHeight - rect.bottom - 18
      const above = rect.top - 18
      const up = below < 240 && above > below
      setPosition({ position: 'fixed', width, maxWidth: 'none',
        left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), right: 'auto',
        top: up ? 'auto' : rect.bottom + 6, bottom: up ? window.innerHeight - rect.top + 6 : 'auto',
        maxHeight: Math.max(60, Math.min(384, up ? above : below)) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [expanded])

  useEffect(() => {
    if (!expanded) return
    const buttons = menu.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)')
    const chosen = menu.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]:not(:disabled)')
    ;(chosen ?? buttons?.[0])?.focus()
    const outside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node) && !menu.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [expanded])

  function close() { setOpen(false); trigger.current?.focus() }
  function navigate(event: KeyboardEvent<HTMLDivElement>) {
    const buttons = [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ?? [])]
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement)
    let next: number | undefined
    if (event.key === 'ArrowDown') next = (index + 1) % buttons.length
    if (event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length
    if (event.key === 'Home') next = 0
    if (event.key === 'End') next = buttons.length - 1
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() }
    if (event.key === 'Tab') close()
    if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const text = (Date.now() - search.current.time < 700 ? search.current.text : '') + event.key.toLowerCase()
      search.current = { text, time: Date.now() }
      const match = buttons.findIndex(button => button.dataset.label?.toLowerCase().startsWith(text))
      if (match >= 0) next = match
    }
    if (next !== undefined) { event.preventDefault(); buttons[next]?.focus() }
  }
  return <span className={`review-picker${field ? ' picker-field' : ''}`} ref={container}
    onBlur={event => {
      if (!container.current?.contains(event.relatedTarget) && !menu.current?.contains(event.relatedTarget)) setOpen(false)
    }}>
    <button ref={trigger} id={id} type="button" className={`review-picker-trigger ${triggerClassName ?? ''}`} disabled={disabled || !options.length}
      aria-label={triggerContent ? label : `${label}: ${selectedLabel}`} aria-haspopup="menu" aria-expanded={expanded}
      aria-invalid={invalid && !(props.multiple ? props.value.length : props.value) || undefined} aria-controls={expanded ? `${id}-menu` : undefined}
      onClick={() => setOpen(current => !current)} onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true) }
      }}>
      <span className="picker-value">{triggerContent ?? selected?.content ?? selectedLabel}</span>
      <span className="review-picker-chevron" aria-hidden="true" />
    </button>
    {required && <input className="visually-hidden" aria-hidden="true" tabIndex={-1} required disabled={disabled}
      value={props.multiple ? props.value.join(',') : props.value} onChange={() => {}} onInvalid={event => { event.preventDefault(); setInvalid(true); trigger.current?.focus(); setOpen(true) }} />}
    {expanded && createPortal(<div id={`${id}-menu`} ref={menu} className="review-picker-menu picker-popup" style={position}
      role="menu" aria-label={label} onKeyDown={navigate}>
      {props.multiple && <>
        <button type="button" role="menuitemcheckbox" aria-checked={allSelected ? true : picked.length ? 'mixed' : false}
          data-label="Select all" tabIndex={-1} onClick={() => props.onChange(allSelected ? [] : allValues)}>
          <span className="review-picker-option">Select all</span>
          {(allSelected || picked.length > 0) && <Icon name={allSelected ? 'check' : 'minus'} size={16} />}
        </button>
        <div role="separator" className="picker-separator" />
      </>}
      {options.map(option => <button key={option.value} type="button" role={props.multiple ? "menuitemcheckbox" : "menuitemradio"} data-label={option.label}
        aria-checked={picked.includes(option.value)} disabled={option.disabled} tabIndex={-1}
        onClick={event => {
          event.stopPropagation(); setInvalid(false)
          if (props.multiple) props.onChange(picked.includes(option.value) ? picked.filter(item => item !== option.value) : [...picked, option.value])
          else { props.onChange(option.value); close() }
        }}>
        <span className="review-picker-option"><span>{option.content ?? option.label}</span>
          {option.detail && <span className="review-picker-meta">{option.detail}</span>}</span>
        {picked.includes(option.value) && <Icon name="check" size={16} />}
      </button>)}
    </div>, document.body)}
  </span>
}
